import { i } from "@instantdb/react";

const _schema = i.schema({
  entities: {
    // === System ===
    $users: i.entity({
      email: i.string().unique().indexed(),
    }),
    $files: i.entity({
      path: i.string().unique().indexed(),
      url: i.string(),
    }),

    // === Sales ===
    dealers: i.entity({
      _id: i.string().unique().indexed(),
      code: i.string().unique().indexed(),
      legalName: i.string().optional(),
      displayName: i.string().indexed(),
      tierId: i.string().indexed(),
      branchId: i.string().indexed(),
      dealerAreaId: i.string().indexed().optional(),
      primaryUserId: i.string().optional(),
      creditLimitCents: i.string().indexed(),
      creditLimitCurrency: i.string(),
      creditUsedCents: i.string().indexed(),
      paymentTermsDays: i.number(),
      addressJson: i.json(),
      contactJson: i.json(),
      isActive: i.boolean().indexed(),
      createdAt: i.number().indexed(),
      updatedAt: i.number().indexed(),
      openingBalanceBaseCents: i.string().optional(),
      openingBalanceAsOf: i.number().optional(),
      importSource: i.string().optional(),
    }),
    dealerTiers: i.entity({
      _id: i.string().unique().indexed(),
      code: i.string().unique().indexed(),
      displayName: i.string().indexed(),
      discountBp: i.number(),
      pointsMultiplierE4: i.string().indexed(),
      sortOrder: i.number(),
      isActive: i.boolean().indexed(),
    }),
    salesOrders: i.entity({
      _id: i.string().unique().indexed(),
      orderNumber: i.string().unique().indexed(),
      dealerId: i.string().indexed(),
      branchId: i.string().indexed(),
      placedByUserId: i.string().indexed(),
      approvedByUserId: i.string().indexed().optional(),
      status: i.string().indexed(),
      placedAt: i.number().indexed(),
      approvedAt: i.number().optional(),
      shippedAt: i.number().optional(),
      deliveredAt: i.number().optional(),
      requestedDeliveryDate: i.number().optional(),
      currency: i.string(),
      fxRateToBaseE8: i.string().indexed(),
      amountCents: i.string().indexed(),
      discountInBaseCents: i.string().indexed(),
      taxInBaseCents: i.string().indexed(),
      totalInBaseCents: i.string().indexed(),
      pointsEarned: i.string().indexed(),
      pointsRedeemed: i.string().indexed(),
      notes: i.string().optional(),
      createdAt: i.number().indexed(),
      updatedAt: i.number().indexed(),
    }),
    salesOrderLines: i.entity({
      _id: i.string().unique().indexed(),
      salesOrderId: i.string().indexed(),
      productId: i.string().indexed(),
      variantId: i.string().indexed().optional(),
      unitId: i.string().indexed(),
      taxRateId: i.string().indexed().optional(),
      quantityOrdered: i.number(),
      unitPriceCents: i.string().indexed(),
      lineTotalInBaseCents: i.string().indexed(),
      currency: i.string(),
      sortOrder: i.number(),
    }),
    salesOrderStatusHistory: i.entity({
      _id: i.string().unique().indexed(),
      salesOrderId: i.string().indexed(),
      changedByUserId: i.string().indexed(),
      fromStatus: i.string().indexed().optional(),
      toStatus: i.string().indexed(),
      changedAt: i.number().indexed(),
      reason: i.string().optional(),
    }),
    dealerOffers: i.entity({
      _id: i.string().unique().indexed(),
      dealerId: i.string().indexed(),
      productId: i.string().indexed(),
      variantId: i.string().indexed().optional(),
      unitPriceCents: i.string().indexed(),
      currency: i.string(),
      minQuantity: i.number(),
      validFrom: i.number().indexed(),
      validUntil: i.number().optional(),
      isActive: i.boolean().indexed(),
    }),

    // === Invoicing ===
    invoices: i.entity({
      _id: i.string().unique().indexed(),
      invoiceNumber: i.string().indexed(),
      salesOrderId: i.string().indexed().optional(),
      dealerId: i.string().indexed(),
      branchId: i.string().indexed(),
      status: i.string().indexed(),
      currency: i.string(),
      fxRateToBaseE8: i.string().indexed(),
      subtotalAmountCents: i.string().indexed(),
      discountAmountCents: i.string().indexed(),
      taxAmountCents: i.string().indexed(),
      shippingAmountCents: i.string().indexed(),
      totalAmountCents: i.string().indexed(),
      totalAmountInBaseCents: i.string().indexed(),
      paidAmountCents: i.string().indexed(),
      paidAmountInBaseCents: i.string().indexed(),
      balanceDueCents: i.string().indexed(),
      // New unified invoice-level discount model. `generalDiscountValue` is a
      // BigInt-as-string and is interpreted by `generalDiscountType`:
      //   - "percent" → bp×100 (E4) of the invoice subtotal
      //   - "flat"    → cents subtracted directly from the invoice subtotal
      generalDiscountType: i.string().indexed().optional(),
      generalDiscountValue: i.string().indexed().optional(),
      // New VAT toggle + snapshot. Rate comes from companyProfile.defaultVatRatePercentE4
      // at issue time; the computed VAT amount is stored here for reporting.
      vatEnabled: i.boolean().indexed(),
      vatAmountCents: i.string().indexed().optional(),
      // ── Back-compat (deprecated) ─────────────────────────────────────────
      generalDiscountCents: i.string().indexed().optional(),
      generalDiscountBp: i.number().optional(),
      generalDiscountReason: i.string().optional(),
      issueDate: i.number().indexed(),
      dueDate: i.number().indexed(),
      issuedAt: i.number().optional(),
      voidedAt: i.number().optional(),
      voidReason: i.string().optional(),
      notes: i.string().optional(),
      createdAt: i.number().indexed(),
      updatedAt: i.number().indexed(),
    }),
    invoiceLines: i.entity({
      _id: i.string().unique().indexed(),
      invoiceId: i.string().indexed(),
      productId: i.string().indexed(),
      productVariantId: i.string().indexed().optional(),
      unitId: i.string().indexed(),
      // Per-line tax is dropped from the new model — VAT is now invoice-level
      // and toggled via `invoices.vatEnabled`. Kept optional for back-compat.
      taxRateId: i.string().indexed().optional(),
      description: i.string().optional(),
      quantity: i.number(),
      unitPriceCents: i.string().indexed(),
      netUnitPriceCents: i.string().indexed(),
      // New unified per-line discount model. `discountValue` is a BigInt-as-string
      // and is interpreted by `discountType`:
      //   - "percent" → bp×100 (E4) of the line subtotal
      //   - "flat"    → cents subtracted directly from the line subtotal
      discountType: i.string().indexed().optional(),
      discountValue: i.string().indexed().optional(),
      // ── Back-compat (deprecated) ─────────────────────────────────────────
      discountPercentE4: i.string().indexed().optional(),
      lineDiscountBp: i.number().optional(),
      taxRatePercentE4: i.string().indexed().optional(),
      lineSubtotalCents: i.string().indexed(),
      lineDiscountCents: i.string().indexed(),
      lineTaxCents: i.string().indexed().optional(),
      lineTotalCents: i.string().indexed(),
      fxRateToBaseE8: i.string().indexed(),
      lineTotalInBaseCents: i.string().indexed(),
      sortOrder: i.number(),
    }),
    payments: i.entity({
      _id: i.string().unique().indexed(),
      paymentNumber: i.string().indexed(),
      invoiceId: i.string().indexed(),
      creditNoteId: i.string().indexed().optional(),
      paymentMethod: i.string().indexed(),
      currency: i.string(),
      amountCents: i.string().indexed(),
      fxRateToBaseE8: i.string().indexed(),
      amountInBaseCents: i.string().indexed(),
      externalReference: i.string().optional(),
      receivedAt: i.number().indexed(),
      bankAccountName: i.string().optional(),
      notes: i.string().optional(),
      voidedAt: i.number().indexed().optional(),
    }),
    creditNotes: i.entity({
      _id: i.string().unique().indexed(),
      creditNoteNumber: i.string().indexed(),
      dealerId: i.string().indexed(),
      originalInvoiceId: i.string().indexed().optional(),
      creditReason: i.string(),
      currency: i.string(),
      status: i.string().indexed(),
      amountCents: i.string().indexed(),
      fxRateToBaseE8: i.string().indexed(),
      amountInBaseCents: i.string().indexed(),
      remainingAmountCents: i.string().indexed(),
      remainingAmountInBaseCents: i.string().indexed(),
      issueDate: i.number().indexed(),
      expiryDate: i.number().optional(),
      notes: i.string().optional(),
    }),

    // === Catalog ===
    brands: i.entity({
      _id: i.string().unique().indexed(),
      code: i.string().unique().indexed(),
      displayName: i.string().indexed(),
      description: i.string().optional(),
      isActive: i.boolean().indexed(),
    }),
    categories: i.entity({
      _id: i.string().unique().indexed(),
      code: i.string().unique().indexed(),
      displayName: i.string().indexed(),
      parentCategoryId: i.string().indexed().optional(),
      sortOrder: i.number(),
      isActive: i.boolean().indexed(),
    }),
    units: i.entity({
      _id: i.string().unique().indexed(),
      code: i.string().unique().indexed(),
      displayName: i.string().indexed(),
      dimension: i.string().indexed(),
      conversionFactor: i.number(),
      isActive: i.boolean().indexed(),
      decimalPlaces: i.number().optional(),
      isArchived: i.boolean().indexed().optional(),
    }),
    taxRates: i.entity({
      _id: i.string().unique().indexed(),
      code: i.string().unique().indexed(),
      displayName: i.string().indexed(),
      taxType: i.string().indexed(),
      ratePercentE4: i.string().indexed(),
      isActive: i.boolean().indexed(),
      isDefault: i.boolean().indexed(),
      jurisdiction: i.string().optional(),
      deletedAt: i.number().optional(),
    }),
    products: i.entity({
      _id: i.string().unique().indexed(),
      sku: i.string().unique().indexed(),
      displayName: i.string().indexed(),
      description: i.string().optional(),
      productKind: i.string().indexed(),
      brandId: i.string().indexed().optional(),
      categoryId: i.string().indexed().optional(),
      primaryUnitId: i.string().indexed(),
      defaultTaxRateId: i.string().indexed().optional(),
      standardCostCents: i.string().indexed(),
      hsCode: i.string().optional(),
      reorderQuantity: i.number().optional(),
      barcodePrimary: i.string().optional(),
      isActive: i.boolean().indexed(),
      createdAt: i.number().indexed(),
      updatedAt: i.number().indexed(),
    }),
    productVariants: i.entity({
      _id: i.string().unique().indexed(),
      productId: i.string().indexed(),
      variantSku: i.string().unique().indexed(),
      variantName: i.string().indexed(),
      attributesJson: i.string(),
      unitId: i.string().indexed().optional(),
      barcode: i.string().optional(),
      weightGrams: i.number().optional(),
      isActive: i.boolean().indexed(),
      updatedAt: i.number().indexed(),
    }),
    boms: i.entity({
      _id: i.string().unique().indexed(),
      code: i.string().indexed(),
      productId: i.string().indexed(),
      productVariantId: i.string().indexed().optional(),
      outputUnitId: i.string().indexed(),
      versionLabel: i.string(),
      isActive: i.boolean().indexed(),
      outputQuantity: i.number(),
      notes: i.string().optional(),
      activatedAt: i.number().optional(),
      createdAt: i.number().indexed(),
      updatedAt: i.number().indexed(),
    }),
    bomLines: i.entity({
      _id: i.string().unique().indexed(),
      bomId: i.string().indexed(),
      componentProductId: i.string().indexed(),
      componentVariantId: i.string().indexed().optional(),
      unitId: i.string().indexed(),
      quantity: i.number(),
      wastageAllowanceE4: i.string().indexed(),
      notes: i.string().optional(),
      sortOrder: i.number(),
    }),

    // === Inventory ===
    branches: i.entity({
      _id: i.string().unique().indexed(),
      code: i.string().unique().indexed(),
      displayName: i.string().indexed(),
      branchType: i.string().indexed(),
      city: i.string(),
      addressLine: i.string(),
      contactPhone: i.string().optional(),
      isActive: i.boolean().indexed(),
      deletedAt: i.number().optional(),
    }),
    warehouses: i.entity({
      _id: i.string().unique().indexed(),
      code: i.string().unique().indexed(),
      displayName: i.string().indexed(),
      branchId: i.string().indexed(),
      warehouseType: i.string().indexed(),
      isActive: i.boolean().indexed(),
    }),
    stockMovements: i.entity({
      _id: i.string().unique().indexed(),
      productId: i.string().indexed(),
      productVariantId: i.string().indexed().optional(),
      warehouseId: i.string().indexed(),
      movementType: i.string().indexed(),
      quantityDelta: i.number(),
      quantityBalanceAfter: i.number(),
      unitCostBaseCents: i.string().indexed(),
      valueDeltaBaseCents: i.string().indexed(),
      referenceType: i.string().indexed().optional(),
      referenceId: i.string().indexed().optional(),
      occurredAt: i.number().indexed(),
      notes: i.string().optional(),
    }),
    stockReservations: i.entity({
      _id: i.string().unique().indexed(),
      productId: i.string().indexed(),
      productVariantId: i.string().indexed().optional(),
      warehouseId: i.string().indexed(),
      quantityReserved: i.number(),
      referenceType: i.string().indexed(),
      referenceId: i.string().indexed(),
      status: i.string().indexed(),
    }),

    // === Workforce ===
    employees: i.entity({
      _id: i.string().unique().indexed(),
      code: i.string().unique().indexed(),
      fullName: i.string().indexed(),
      displayName: i.string().indexed(),
      photoUrl: i.string().optional(),
      userId: i.string().indexed().optional(),
      branchId: i.string().indexed().optional(),
      employmentType: i.string().indexed(),
      department: i.string().indexed(),
      branchLabel: i.string().optional(),
      jobTitle: i.string(),
      hiredAt: i.number().indexed(),
      hiredAtIsOverride: i.boolean(),
      terminatedAt: i.number().optional(),
      baseSalaryCents: i.string().indexed(),
      salaryCurrency: i.string(),
      payCadence: i.string(),
      nationalId: i.string().optional(),
      phone: i.string(),
      email: i.string().indexed().optional(),
      addressLine1: i.string(),
      addressLine2: i.string().optional(),
      addressCity: i.string(),
      addressDistrict: i.string(),
      addressPostalCode: i.string().optional(),
      emergencyContact: i.json(),
      bankAccount: i.json(),
      notes: i.string().optional(),
      isActive: i.boolean().indexed(),
      createdAt: i.number().indexed(),
      updatedAt: i.number().indexed(),
    }),
    attendance: i.entity({
      _id: i.string().unique().indexed(),
      employeeId: i.string().indexed(),
      date: i.number().indexed(),
      status: i.string().indexed(),
      checkInAt: i.number().optional(),
      checkOutAt: i.number().optional(),
      workedMinutes: i.number(),
      overtimeMinutes: i.number(),
      notes: i.string().optional(),
    }),
    leaves: i.entity({
      _id: i.string().unique().indexed(),
      employeeId: i.string().indexed(),
      approvedByUserId: i.string().indexed().optional(),
      leaveType: i.string().indexed(),
      fromDate: i.number().indexed(),
      toDate: i.number().indexed(),
      days: i.number(),
      reason: i.string().optional(),
      status: i.string().indexed(),
      approvedAt: i.number().optional(),
      rejectionReason: i.string().optional(),
    }),
    employeePayments: i.entity({
      _id: i.string().unique().indexed(),
      employeeId: i.string().indexed(),
      paymentType: i.string().indexed(),
      periodFrom: i.number().indexed(),
      periodTo: i.number().indexed(),
      currency: i.string(),
      baseSalaryCents: i.string().indexed(),
      overtimeCents: i.string().indexed(),
      bonusCents: i.string().indexed(),
      deductionsCents: i.string().indexed(),
      netCents: i.string().indexed(),
      fxRateToBaseE8: i.string().indexed(),
      netInBaseCents: i.string().indexed(),
      paidAt: i.number().indexed().optional(),
      paymentMethod: i.string().indexed(),
      referenceNumber: i.string().optional(),
      status: i.string().indexed(),
      notes: i.string().optional(),
    }),

    // === Finance ===
    loans: i.entity({
      _id: i.string().unique().indexed(),
      loanNumber: i.string().unique().indexed(),
      loanKind: i.string().indexed(),
      counterpartyName: i.string().indexed(),
      direction: i.string().indexed(),
      currency: i.string(),
      principalCents: i.string().indexed(),
      outstandingPrincipalCents: i.string().indexed(),
      fxRateToBaseE8: i.string().indexed(),
      amountInBaseCents: i.string().indexed(),
      outstandingPrincipalInBaseCents: i.string().indexed(),
      interestType: i.string().indexed(),
      interestRatePercentE4: i.string().indexed(),
      scheduleKind: i.string().indexed(),
      tenorPeriods: i.number(),
      issuedDate: i.number().indexed(),
      dueDate: i.number().optional(),
      status: i.string().indexed(),
      notes: i.string().optional(),
    }),
    loanRepayments: i.entity({
      _id: i.string().unique().indexed(),
      repaymentNumber: i.string().indexed(),
      loanId: i.string().indexed(),
      occurredAt: i.number().indexed(),
      currency: i.string(),
      principalCents: i.string().indexed(),
      interestCents: i.string().indexed(),
      totalCents: i.string().indexed(),
      paymentMethod: i.string().indexed(),
    }),
    expenses: i.entity({
      _id: i.string().unique().indexed(),
      expenseNumber: i.string().unique().indexed(),
      category: i.string().indexed(),
      description: i.string(),
      branchId: i.string().indexed().optional(),
      branchLabel: i.string(),
      currency: i.string(),
      amountCents: i.string().indexed(),
      amountInBaseCents: i.string().indexed(),
      fxRateToBaseE8: i.string().indexed(),
      paymentMethod: i.string().indexed(),
      payeeName: i.string().optional(),
      occurredAt: i.number().indexed(),
      status: i.string().indexed(),
      notes: i.string().optional(),
    }),
    fxRates: i.entity({
      _id: i.string().unique().indexed(),
      fromCurrency: i.string().indexed(),
      toCurrency: i.string().indexed(),
      rateE8: i.string().indexed(),
      effectiveAt: i.number().indexed(),
      source: i.string().indexed(),
      notes: i.string().optional(),
    }),

    // === GL ===
    accounts: i.entity({
      _id: i.string().unique().indexed(),
      code: i.string().unique().indexed(),
      name: i.string().indexed(),
      accountType: i.string().indexed(),
      parentAccountId: i.string().indexed().optional(),
      currency: i.string(),
      currentBalanceInBaseCents: i.string().indexed(),
      isActive: i.boolean().indexed(),
      description: i.string().optional(),
      normalBalance: i.string().indexed(),
    }),
    journalEntries: i.entity({
      _id: i.string().unique().indexed(),
      entryNumber: i.string().unique().indexed(),
      entryDate: i.number().indexed(),
      description: i.string(),
      status: i.string().indexed(),
      sourceTable: i.string().indexed().optional(),
      sourceId: i.string().indexed().optional(),
      postedAt: i.number().indexed().optional(),
      reversedByEntryId: i.string().indexed().optional(),
      reversalReason: i.string().optional(),
    }),
    journalLines: i.entity({
      _id: i.string().unique().indexed(),
      journalEntryId: i.string().indexed(),
      accountId: i.string().indexed(),
      entryDate: i.number().indexed(),
      currency: i.string(),
      debitInBaseCents: i.string().indexed(),
      creditInBaseCents: i.string().indexed(),
      originalAmountCents: i.string().indexed(),
      fxRateToBaseE8: i.string().indexed(),
      description: i.string().optional(),
      sortOrder: i.number(),
    }),

    // === Payables ===
    supplierPayments: i.entity({
      _id: i.string().unique().indexed(),
      paymentNumber: i.string().unique().indexed(),
      supplierId: i.string().indexed(),
      purchaseOrderId: i.string().indexed().optional(),
      occurredAt: i.number().indexed(),
      currency: i.string(),
      amountCents: i.string().indexed(),
      amountInBaseCents: i.string().indexed(),
      paymentMethod: i.string().indexed(),
      reference: i.string().optional(),
    }),
    supplierInvoices: i.entity({
      _id: i.string().unique().indexed(),
      invoiceNumber: i.string().unique().indexed(),
      supplierId: i.string().indexed(),
      purchaseOrderId: i.string().indexed(),
      issuedAt: i.number().indexed(),
      currency: i.string(),
      amountCents: i.string().indexed(),
      amountInBaseCents: i.string().indexed(),
      status: i.string().indexed(),
    }),
    suppliers: i.entity({
      _id: i.string().unique().indexed(),
      code: i.string().unique().indexed(),
      displayName: i.string().indexed(),
      legalName: i.string().optional(),
      countryCode: i.string().indexed(),
      defaultCurrency: i.string(),
      contactPersonName: i.string().optional(),
      contactEmail: i.string().optional(),
      contactPhone: i.string().optional(),
      addressJson: i.string(),
      taxRegistrationNumber: i.string().optional(),
      bankDetailsJson: i.string().optional(),
      paymentTermsDays: i.number(),
      isActive: i.boolean().indexed(),
      createdAt: i.number().indexed(),
      updatedAt: i.number().indexed(),
      deletedAt: i.number().optional(),
    }),

    // === Pricing ===
    priceLists: i.entity({
      _id: i.string().unique().indexed(),
      code: i.string().unique().indexed(),
      displayName: i.string().indexed(),
      currency: i.string(),
      defaultForDealerTierId: i.string().indexed().optional(),
      isActive: i.boolean().indexed(),
      validFrom: i.number().indexed(),
      validTo: i.number().optional(),
      notes: i.string().optional(),
      createdAt: i.number().indexed(),
      updatedAt: i.number().indexed(),
    }),
    priceListItems: i.entity({
      _id: i.string().unique().indexed(),
      priceListId: i.string().indexed(),
      productId: i.string().indexed(),
      productVariantId: i.string().indexed().optional(),
      unitPriceCents: i.string().indexed(),
      minQuantity: i.number(),
      bulkTiersJson: i.string().optional(),
      isActive: i.boolean().indexed(),
    }),
    dealerPriceListAssignments: i.entity({
      _id: i.string().unique().indexed(),
      dealerId: i.string().indexed(),
      priceListId: i.string().indexed(),
      validFrom: i.number().indexed(),
      validTo: i.number().optional(),
      isActive: i.boolean().indexed(),
    }),

    // === Dealer Areas ===
    dealerAreas: i.entity({
      _id: i.string().unique().indexed(),
      code: i.string().unique().indexed(),
      displayName: i.string().indexed(),
      branchId: i.string().indexed(),
      district: i.string().indexed(),
      description: i.string(),
      isActive: i.boolean().indexed(),
      createdAt: i.number().indexed(),
      updatedAt: i.number().indexed(),
      deletedAt: i.number().optional(),
    }),

    // === Production ===
    workOrders: i.entity({
      _id: i.string().unique().indexed(),
      workOrderNumber: i.string().unique().indexed(),
      outputProductId: i.string().indexed(),
      outputVariantId: i.string().indexed().optional(),
      bomId: i.string().indexed(),
      outputUnitId: i.string().indexed(),
      factoryWarehouseId: i.string().indexed(),
      outputWarehouseId: i.string().indexed(),
      assignedSupervisorUserId: i.string().indexed().optional(),
      createdByUserId: i.string().indexed().optional(),
      updatedByUserId: i.string().indexed().optional(),
      plannedQuantity: i.number(),
      actualQuantity: i.number(),
      plannedStartAt: i.number().indexed(),
      plannedEndAt: i.number().indexed(),
      actualStartedAt: i.number().optional(),
      actualCompletedAt: i.number().optional(),
      status: i.string().indexed(),
      priority: i.string().indexed(),
      notes: i.string().optional(),
      createdAt: i.number().indexed(),
      updatedAt: i.number().indexed(),
    }),
    workOrderConsumptions: i.entity({
      _id: i.string().unique().indexed(),
      workOrderId: i.string().indexed(),
      bomLineId: i.string().indexed().optional(),
      componentProductId: i.string().indexed(),
      componentVariantId: i.string().indexed().optional(),
      unitId: i.string().indexed(),
      consumedFromWarehouseId: i.string().indexed(),
      plannedQuantity: i.number(),
      actualQuantity: i.number(),
      costPerUnitInBaseCents: i.string().indexed(),
      createdAt: i.number().indexed(),
    }),
    workOrderProductions: i.entity({
      _id: i.string().unique().indexed(),
      workOrderId: i.string().indexed(),
      producedAt: i.number().indexed(),
      quantity: i.number(),
      qualityGrade: i.string().indexed(),
      landedCostInBaseCents: i.string().indexed(),
      batchId: i.string().optional(),
      createdAt: i.number().indexed(),
    }),

    // === Imports ===
    lettersOfCredit: i.entity({
      _id: i.string().unique().indexed(),
      lcNumber: i.string().unique().indexed(),
      supplierId: i.string().indexed(),
      bankName: i.string().indexed(),
      bankReference: i.string().optional(),
      status: i.string().indexed(),
      issueDate: i.number().indexed(),
      expiryDate: i.number().indexed(),
      incoterms: i.string().indexed(),
      currency: i.string(),
      amountCents: i.string().indexed(),
      fxRateToBaseE8: i.string().indexed(),
      amountInBaseCents: i.string().indexed(),
      drawnAmountCents: i.string().indexed(),
      drawnAmountInBaseCents: i.string().indexed(),
      notes: i.string().optional(),
    }),
    purchaseOrders: i.entity({
      _id: i.string().unique().indexed(),
      poNumber: i.string().unique().indexed(),
      supplierId: i.string().indexed(),
      letterOfCreditId: i.string().indexed().optional(),
      branchId: i.string().indexed(),
      destinationWarehouseId: i.string().indexed(),
      submittedByUserId: i.string().indexed().optional(),
      approvedByUserId: i.string().indexed().optional(),
      status: i.string().indexed(),
      currency: i.string(),
      fxRateToBaseE8: i.string().indexed(),
      subtotalAmountCents: i.string().indexed(),
      taxAmountCents: i.string().indexed(),
      shippingAmountCents: i.string().indexed(),
      totalAmountCents: i.string().indexed(),
      totalAmountInBaseCents: i.string().indexed(),
      orderDate: i.number().indexed(),
      expectedDeliveryDate: i.number().indexed().optional(),
      incoterms: i.string().indexed(),
      notes: i.string().optional(),
      submittedAt: i.number().optional(),
      approvedAt: i.number().optional(),
    }),
    shipments: i.entity({
      _id: i.string().unique().indexed(),
      shipmentNumber: i.string().unique().indexed(),
      purchaseOrderId: i.string().indexed().optional(),
      supplierId: i.string().indexed(),
      receivedAtWarehouseId: i.string().indexed().optional(),
      status: i.string().indexed(),
      transportMode: i.string().indexed(),
      carrierName: i.string().optional(),
      blAwbNumber: i.string().optional(),
      containerNumbers: i.json(),
      portOfLoading: i.string().optional(),
      portOfDischarge: i.string().optional(),
      etd: i.number().indexed().optional(),
      eta: i.number().indexed().optional(),
      actualArrivalDate: i.number().indexed().optional(),
      landedCostBaseCents: i.string().indexed(),
      notes: i.string().optional(),
    }),

    // === Settings Singletons ===
    companyProfile: i.entity({
      _id: i.string().unique().indexed(),
      legalName: i.string(),
      displayName: i.string().indexed(),
      baseCurrency: i.string(),
      fiscalYearStartMonth: i.number(),
      addressLine1: i.string(),
      addressCity: i.string(),
      addressCountry: i.string(),
      taxRegistrationNumber: i.string(),
      websiteUrl: i.string(),
      contactEmail: i.string(),
      contactPhone: i.string(),
      invoicePrefix: i.string(),
      invoiceYearFormat: i.string().indexed(),
      invoicePadLength: i.number(),
      // Default VAT rate applied when `invoices.vatEnabled` is true.
      // Stored as BigInt-as-string in E4 scaling (e.g. 150000 = 15%).
      defaultVatRatePercentE4: i.string().indexed(),
    }),

    // === Notifications / Attachments / Audit / Roles / Users ===
    notifications: i.entity({
      _id: i.string().unique().indexed(),
      kind: i.string().indexed(),
      title: i.string(),
      body: i.string().optional(),
      entityType: i.string().indexed().optional(),
      entityId: i.string().indexed().optional(),
      actionUrl: i.string().optional(),
      isRead: i.boolean().indexed(),
      readAt: i.number().optional(),
      createdAt: i.number().indexed(),
    }),
    attachments: i.entity({
      _id: i.string().unique().indexed(),
      parentTable: i.string().indexed(),
      parentRecordId: i.string().indexed(),
      uploadedByUserId: i.string().indexed().optional(),
      storageId: i.string().indexed(),
      externalUrl: i.string().optional(),
      fileName: i.string().indexed(),
      mimeType: i.string().indexed(),
      sizeBytes: i.number(),
      purpose: i.string().indexed(),
      uploadedAt: i.number().indexed(),
      isDeleted: i.boolean().indexed(),
      createdAt: i.number().indexed(),
      updatedAt: i.number().indexed(),
    }),
    auditEntries: i.entity({
      _id: i.string().unique().indexed(),
      tableName: i.string().indexed(),
      recordId: i.string().indexed(),
      actorUserId: i.string().indexed().optional(),
      action: i.string().indexed(),
      beforeJson: i.string().optional(),
      afterJson: i.string().optional(),
      diffJson: i.string(),
      occurredAt: i.number().indexed(),
      ipAddress: i.string().optional(),
      userAgent: i.string().optional(),
      notes: i.string().optional(),
    }),
    roles: i.entity({
      _id: i.string().unique().indexed(),
      key: i.string().unique().indexed(),
      displayName: i.string().indexed(),
      description: i.string(),
      // Stored as JSON array directly (was permissionsJson string before)
      permissions: i.json(),
      isSystem: i.boolean().indexed(),
    }),
    officeUsers: i.entity({
      _id: i.string().unique().indexed(),
      fullName: i.string().indexed(),
      email: i.string().unique().indexed(),
      userType: i.string().indexed(),
      roleKey: i.string().indexed(),
      branchId: i.string().indexed().optional(),
      branchLabel: i.string().optional(),
      lastLoginAt: i.number().optional(),
      isActive: i.boolean().indexed(),
      createdAt: i.number().indexed(),
    }),

    // === Sales Operations ===
    returns: i.entity({
      _id: i.string().unique().indexed(),
      returnNumber: i.string().unique().indexed(),
      dealerId: i.string().indexed(),
      invoiceId: i.string().indexed().optional(),
      status: i.string().indexed(),
      reason: i.string().indexed(),
      items: i.string(),                     // BigInt-safe JSON string (bigints encoded as "__B:N")
      totalAmountCents: i.string().indexed(), // BigInt encoded as string
      creditNoteId: i.string().indexed().optional(),
      notes: i.string(),
      requestedAt: i.number().indexed(),
      resolvedAt: i.number().optional(),
      createdBy: i.string().indexed(),
      createdAt: i.number().indexed(),
      updatedAt: i.number().indexed(),
    }),
    deliveries: i.entity({
      _id: i.string().unique().indexed(),
      deliveryNumber: i.string().unique().indexed(),
      invoiceId: i.string().indexed().optional(),
      invoiceNumber: i.string().indexed(),
      dealerId: i.string().indexed(),
      dealerName: i.string(),
      status: i.string().indexed(),
      items: i.json(),
      totalItems: i.number(),
      dispatchedAt: i.number().optional(),
      deliveredAt: i.number().optional(),
      expectedDeliveryAt: i.number().indexed(),
      driverName: i.string(),
      driverPhone: i.string(),
      vehicleNumber: i.string(),
      deliveryAddress: i.string(),
      notes: i.string(),
      proofOfDelivery: i.boolean().indexed(),
      createdAt: i.number().indexed(),
      updatedAt: i.number().indexed(),
    }),
    stockTransfers: i.entity({
      _id: i.string().unique().indexed(),
      transferNumber: i.string().unique().indexed(),
      fromWarehouseId: i.string().indexed(),
      toWarehouseId: i.string().indexed(),
      status: i.string().indexed(),
      items: i.string(),                     // BigInt-safe JSON string
      totalItems: i.number(),
      totalValueCents: i.string().indexed(), // BigInt encoded as string
      requestedBy: i.string().indexed(),
      approvedBy: i.string().indexed().optional(),
      shippedAt: i.number().optional(),
      receivedAt: i.number().optional(),
      notes: i.string(),
      createdAt: i.number().indexed(),
      updatedAt: i.number().indexed(),
    }),
  },
  rooms: {},
});

type _AppSchema = typeof _schema;
interface AppSchema extends _AppSchema {}
const schema: AppSchema = _schema;

export type { AppSchema };
export default schema;
